import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProgression } from '../../auth/ProgressionProvider';
import { useSeason } from '../../auth/SeasonProvider';
import { listBuiltinGuitarTracks } from '@shared/builtinGuitarMaps';
import {
  chartDisplayBpm,
  chartDurationSeconds,
  formatDuration,
  stripVariantSuffix,
} from '@shared/chart';
import { VARIANTS, type Variant, variantLabel } from '@shared/gameTypes';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import { isTutorialUnseen } from '../../game/tutorialFlag';
import { TrophyStrip } from '../../components/TrophyStrip';
import { ModeSeoSection } from '../ModeSeoSection';
import '../../components/trophyStrip.css';
import '../home.css';
import './guitarHome.css';

const VARIANT_STORAGE_KEY = 'gravitydash:guitar-home-variant:v1';

function loadStoredVariant(): Variant {
  try {
    const raw = localStorage.getItem(VARIANT_STORAGE_KEY);
    if (raw && (VARIANTS as ReadonlyArray<string>).includes(raw)) {
      return raw as Variant;
    }
  } catch (err) {
    console.warn('Failed to read variant from localStorage:', err);
  }
  return 'normal';
}

function persistVariant(variant: Variant): void {
  try {
    localStorage.setItem(VARIANT_STORAGE_KEY, variant);
  } catch (err) {
    console.warn('Failed to persist variant to localStorage:', err);
  }
}

const VARIANT_DESCRIPTIONS: Record<Variant, string> = {
  normal: 'Beginner-friendly charts. Read the lanes, find the rhythm.',
  hard: 'Denser jacks, chord splits, and mid-tempo streams. Hands stay busy.',
  extreme: 'Top-tier dense charting with no min-spacing floor. Bring your stamina.',
};

/**
 * Guitar-mode home page. Mirrors {@link
 * import('../piano/PianoHomePage.js').PianoHomePage} structurally —
 * variant selector, track list, tutorial gate — but lists the
 * `BUILTIN_GUITAR_TRACKS` catalogue (12 tracks × 3 variants of dense
 * 4-lane charts) instead of Piano's onset-derived synthesised charts.
 */
export function GuitarHomePage() {
  const { forMap } = useProgression();
  const { crownsForMap } = useSeason();
  const tracks = listBuiltinGuitarTracks();
  const navigate = useNavigate();
  const [variant, setVariant] = useState<Variant>(() => loadStoredVariant());

  useEffect(() => {
    saveModePref('guitar');
  }, []);

  useEffect(() => {
    if (isTutorialUnseen('guitar')) {
      navigate(modePath('guitar', 'tutorial'), { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== VARIANT_STORAGE_KEY && event.key !== null) {
        return;
      }
      setVariant(loadStoredVariant());
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const handleVariantChange = (next: Variant) => {
    setVariant(next);
    persistVariant(next);
  };

  const featuredTrack = tracks[0];
  const featuredChart = featuredTrack.variants[variant].chart;

  return (
    <div className="home guitar-home">
      <header className="home-hero">
        <h1 className="home-hero-kicker">
          BEAT<span style={{ color: 'var(--accent-amber, #ffae3b)' }}>GUITAR</span>
        </h1>
        <div className="home-cta">
          <Link
            to={modePath('guitar', `play/${featuredChart.slug}`)}
            className="btn btn-primary btn-large"
            data-testid="guitar-play-cta"
          >
            Play {stripVariantSuffix(featuredChart)}
          </Link>
          <Link
            to={modePath('guitar', 'tutorial')}
            className="btn"
            data-testid="guitar-tutorial-cta"
          >
            How to play
          </Link>
          <Link
            to={modePath('guitar', 'build')}
            className="btn"
            data-testid="guitar-build-cta"
          >
            Create a map
          </Link>
          <Link
            to={modePath('guitar', 'browse')}
            className="btn btn-ghost"
            data-testid="guitar-browse-cta"
          >
            Community maps
          </Link>
          <Link to="/calibrate" className="btn btn-ghost">
            Calibrate audio
          </Link>
        </div>
      </header>

      <section className="home-maps">
        <div className="home-maps-header">
          <h2>Tracks</h2>
          <p className="muted faint">
            Dense 4-lane charts, three difficulty variants each.
          </p>
        </div>

        <div
          className="variant-selector"
          role="group"
          aria-label="Guitar density"
          data-testid="guitar-variant-selector"
        >
          {VARIANTS.map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={variant === v}
              className={`variant-tab variant-tab-${v} ${variant === v ? 'is-active' : ''}`}
              onClick={() => handleVariantChange(v)}
              data-testid={`guitar-variant-tab-${v}`}
            >
              {variantLabel(v)}
            </button>
          ))}
          <p className="variant-hint muted" aria-live="polite">
            {VARIANT_DESCRIPTIONS[variant]}
          </p>
        </div>

        <ol className="map-list">
          {tracks.map((track, idx) => {
            const chart = track.variants[variant].chart;
            const tileCount = chart.notes.length;
            const trophies = forMap(chart.slug, 'guitar');
            return (
              <li key={track.baseSlug} className="map-list-item">
                <Link
                  to={modePath('guitar', `play/${chart.slug}`)}
                  className="map-row guitar-map-row"
                  data-testid={`guitar-map-card-${track.baseSlug}`}
                  style={
                    {
                      '--map-bg': chart.palette.bg,
                      '--map-bg-accent': chart.palette.bgAccent,
                      '--map-pad': chart.palette.pad,
                      '--map-player': chart.palette.player,
                    } as React.CSSProperties
                  }
                >
                  <div className="map-row-index">{String(idx + 1).padStart(2, '0')}</div>
                  <div className="map-row-preview guitar-map-row-preview">
                    {[0, 1, 2, 3].map((laneIdx) => (
                      <div
                        key={laneIdx}
                        className="guitar-preview-lane"
                        style={{ '--lane-index': laneIdx } as React.CSSProperties}
                      />
                    ))}
                  </div>
                  <div className="map-row-body">
                    <div className="map-row-title">{stripVariantSuffix(chart)}</div>
                    <div className="map-row-desc">{chart.description}</div>
                    {chart.mapper && (
                      <div
                        className="map-row-mapper muted faint"
                        data-testid={`guitar-map-mapper-${track.baseSlug}`}
                      >
                        Chart: {chart.mapper}
                      </div>
                    )}
                  </div>
                  <div className="map-row-meta">
                    <div className="map-row-bpm">{chartDisplayBpm(chart.bpm).toFixed(0)} BPM</div>
                    <div className="map-row-duration" title="Duration">
                      {formatDuration(chartDurationSeconds(chart.bpm, chart.totalBeats))}
                    </div>
                    <div
                      className="map-row-pad-count"
                      title={`${tileCount} gems on ${variantLabel(variant)}`}
                    >
                      {tileCount} gems
                    </div>
                    <div
                      className="map-row-difficulty"
                      aria-label={`Difficulty ${chart.difficulty}`}
                    >
                      {Array.from({ length: 5 }, (_, i) => (
                        <span
                          key={i}
                          className={i < chart.difficulty ? 'pip pip-on' : 'pip'}
                        />
                      ))}
                    </div>
                    <TrophyStrip
                      stars={trophies.stars}
                      cups={trophies.cups}
                      crowns={crownsForMap(chart.slug, 'guitar')}
                      size="sm"
                    />
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      </section>

      <ModeSeoSection
        mode="guitar"
        playHref={modePath('guitar', `play/${featuredChart.slug}`)}
        playLabel={`Play ${stripVariantSuffix(featuredChart)}`}
      />
    </div>
  );
}
